<script setup>
import { Timestamp } from 'firebase/firestore';
import { isObject, parseInt } from 'lodash';
import { computed, toRef } from 'vue';

import { useZonedTime } from '../composables/useZonedTime';
import HrTextField from '../primitives/HrTextField.vue';

/**
 * Time field — an HTML time input that reads and writes Firestore Timestamps.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a time-of-day value that is stored as a Firestore Timestamp
 * @event update:modelValue Emitted on change; payload is a Firestore Timestamp, or null when the field is cleared.
 */
defineOptions({ name: 'HrTimeField' });

/**
 * @typedef {'filled' | 'outlined'} HRTimeFieldVariant
 */
const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Firestore Timestamp value. Emits a Timestamp on change.
   */
  modelValue: {
    type: Object,
    default: null,
  },
  /** Floating label text shown above the field. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows the value but prevents editing. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading spinner inside the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Switches the field to the error color role. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /**
   * Validation rules applied to the field value.
   */
  rules: {
    type: Array,
    default: null,
  },
  /**
   * When true, validation runs only after the first blur (not on every keystroke).
   */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /**
   * Suppresses the reserved space below the field for hint/error messages.
   */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /**
   * Reduces the field height to the M3 dense density level.
   */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, keeps the date portion of the existing modelValue when updating the time.
   * When false, uses the current date as the base.
   */
  preserveDate: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, displays an empty field instead of defaulting to 00:00 for null values.
   * Safari requires a value to avoid showing an annoying placeholder.
   */
  allowEmpty: {
    type: Boolean,
    default: false,
  },
  /**
   * IANA timezone the time is read and written in (e.g. "America/New_York"). The application
   * supplies this from its own timezone policy (e.g. a user preference); the field itself has no
   * knowledge of where it comes from. When null, the host's system timezone is used.
   */
  timeZone: {
    type: String,
    default: null,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted on time change; payload is a Firestore Timestamp, or null when the field is cleared. */
  'update:modelValue': null,
});

const { getLocaleTimeString, adjustDate } = useZonedTime(toRef(props, 'timeZone'));

const displayValue = computed(() => {
  if (!isObject(props.modelValue)) {
    // Safari requires a value, or it will show an annoying placeholder
    return props.allowEmpty ? '' : '00:00';
  }
  return getLocaleTimeString(props.modelValue, { options: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } });
});

function handleUpdate(timeValue) {
  if (timeValue == null || typeof timeValue !== 'string') {
    return emit('update:modelValue', null);
  }
  let hours;
  let minutes;

  if (timeValue.length !== 5) {
    if (!props.preserveDate) {
      return emit('update:modelValue', null);
    }
    hours = 0;
    minutes = 0;
  } else {
    [hours, minutes] = timeValue.split(':');
  }

  const base = props.preserveDate && props.modelValue instanceof Timestamp
    ? props.modelValue
    : Date.now();

  const updated = adjustDate(base, { hour: parseInt(hours), minute: parseInt(minutes) });

  if (Number.isNaN(updated.getTime())) {
    return emit('update:modelValue', null);
  }

  emit('update:modelValue', Timestamp.fromDate(updated));
}
</script>

<template>
  <hr-text-field
    :model-value="displayValue"
    :variant="variant"
    :label="label"
    :hint="hint"
    :disable="disable"
    :readonly="readonly"
    :loading="loading"
    :error="error"
    :error-message="errorMessage"
    :rules="rules"
    :lazy-rules="lazyRules"
    :hide-bottom-space="hideBottomSpace"
    :dense="dense"
    :legacy-props="legacyProps"
    type="time"
    stack-label
    @update:model-value="handleUpdate($event)"
  >
    <template
      v-if="$slots.prepend"
      #prepend="slotProps"
    >
      <!-- @slot prepend Content before the field container (e.g. a prefix icon). -->
      <slot
        name="prepend"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.append"
      #append="slotProps"
    >
      <!-- @slot append Content after the field container (e.g. a suffix icon). -->
      <slot
        name="append"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.hint"
      #hint="slotProps"
    >
      <!-- @slot hint Custom hint content below the field. -->
      <slot
        name="hint"
        v-bind="slotProps ?? {}"
      />
    </template>
  </hr-text-field>
</template>
